import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { attachmentsApi, type UploadedAttachment } from '../lib/api/attachments';
import type {
  AdvanceType,
  CreatePaymentInput,
  PaymentRequest,
  PaymentType,
} from '../lib/api/payments';
import { formatVND } from '../lib/format';
import { banksApi, type BankMaster } from '../lib/api/banks';
import {
  computeQcctBudget,
  NHOM_LABEL,
  VUNG_LABEL,
  type QcctNhom,
  type QcctVung,
} from '../lib/qcct';
import { BonusRecipientPicker, type BonusMode, type RecipientEntry } from './BonusRecipientPicker';
import { SearchableDropdown } from './SearchableDropdown';

type AdvancePurpose = 'thuong' | 'congtac';

interface Props {
  open: boolean;
  editing?: PaymentRequest | null;
  /** Nếu set + KHÔNG có `editing`: mở form CREATE với các field pre-fill từ phiếu nguồn (attachments NOT copied). */
  duplicateFrom?: PaymentRequest | null;
  onClose: () => void;
  onSubmit: (input: CreatePaymentInput, submitAfter?: boolean) => Promise<void>;
}

const EMPTY: CreatePaymentInput = {
  amount: 0,
  type: 'chi_thuong',
  reason: '',
  supplier: '',
  department: '',
  bankAccount: '',
  urgency: false,
};

export function PaymentFormModal({ open, editing, duplicateFrom, onClose, onSubmit }: Props) {
  const { user } = useAuth();
  const [form, setForm] = useState<CreatePaymentInput>(EMPTY);
  const [amountInput, setAmountInput] = useState('');
  const [reqDate, setReqDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([]);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [recipients, setRecipients] = useState<RecipientEntry[]>([]);
  const [bonusMode, setBonusMode] = useState<BonusMode>('equal');

  // Mục đích tạm ứng (chỉ dùng khi type=tam_ung) — không persist BE, mock UI theo prototype
  const [advancePurpose, setAdvancePurpose] = useState<AdvancePurpose>('thuong');
  const [ctLocation, setCtLocation] = useState('');
  const [ctDays, setCtDays] = useState<number>(3);
  const [ctNhom, setCtNhom] = useState<QcctNhom>(4);
  const [ctVung, setCtVung] = useState<QcctVung>('HN_HCM');
  const [bm03Search, setBm03Search] = useState('');
  const [banks, setBanks] = useState<BankMaster[]>([]);

  useEffect(() => {
    const source = editing || duplicateFrom;
    if (source) {
      setForm({
        amount: source.amount,
        type: source.type,
        advanceType: source.advanceType || undefined,
        urgency: source.urgency,
        reason: duplicateFrom ? `[Sao chép] ${source.reason}` : source.reason,
        supplier: source.supplier || '',
        department: source.department || user?.departmentName || '',
        bankAccount: source.bankAccount || '',
        recipientBankName: source.recipientBankName || '',
        recipientBankBranch: source.recipientBankBranch || '',
      });
      setAmountInput(source.amount.toLocaleString('vi-VN'));
      // Editing: giữ attachments cũ. Duplicate: bỏ (file link với PR gốc, cần upload lại)
      setAttachments(editing ? (editing.attachments ?? []) : []);
      setRecipients(
        (source.recipients ?? []).map((r) => ({
          employeeId: r.employeeId,
          employeeName: r.employeeName,
          amount: r.amount,
          note: r.note ?? undefined,
        })),
      );
      setBonusMode('manual');
    } else {
      setForm({ ...EMPTY, department: user?.departmentName || '' });
      setAmountInput('');
      setAttachments([]);
      setRecipients([]);
      setBonusMode('equal');
    }
    // default ngày cần chi = 3 ngày sau
    const d = new Date(Date.now() + 3 * 86400000);
    setReqDate(d.toISOString().slice(0, 10));
    setError(null);
    // Reset mục đích tạm ứng — không copy khi duplicate/edit vì chưa persist BE
    setAdvancePurpose('thuong');
    setCtLocation('');
    setCtDays(3);
    setCtNhom(4);
    setCtVung('HN_HCM');
    setBm03Search('');
  }, [editing, duplicateFrom, open, user?.departmentName]);

  useEffect(() => {
    if (!open) return;
    banksApi.list().then((items) => setBanks(items.filter((bank) => bank.active))).catch(() => setBanks([]));
  }, [open]);

  const isAdvance = form.type === 'tam_ung';
  const isBonus = form.type === 'thuong_nong';
  const isAdvanceCT = isAdvance && advancePurpose === 'congtac';
  const isAdvanceThuong = isAdvance && advancePurpose === 'thuong';
  const isSalaryAdvance = form.type === 'ung_luong';
  const showCeoWarn = form.amount >= 3_000_000;
  // Warning chứng từ chỉ áp dụng cho tạm ứng chi phí thường >5M (công tác đã có quyết định)
  // Chỉ warning khi tạm ứng thường > 5M mà CHƯA đính kèm chứng từ.
  const showQuoteWarn = isAdvanceThuong && form.amount > 5_000_000 && attachments.length === 0;

  const ctBudget = useMemo(
    () => (isAdvanceCT ? computeQcctBudget(ctNhom, ctVung, ctDays) : null),
    [isAdvanceCT, ctNhom, ctVung, ctDays],
  );

  // Auto-fill amount khi Công tác thay đổi tham số
  useEffect(() => {
    if (!isAdvanceCT || !ctBudget) return;
    setAmountInput(ctBudget.total.toLocaleString('vi-VN'));
    setForm((prev) => ({ ...prev, amount: ctBudget.total }));
  }, [isAdvanceCT, ctBudget]);

  if (!open) return null;

  const ctExceedsQuota = isAdvanceCT && ctBudget !== null && form.amount > ctBudget.total;
  const initials = (user?.name || '?').split(' ').slice(-2).map((w) => w[0]).join('').toUpperCase();

  const handleAmount = (raw: string) => {
    const digits = raw.replace(/\D/g, '');
    setAmountInput(digits ? Number(digits).toLocaleString('vi-VN') : '');
    setForm({ ...form, amount: digits ? Number(digits) : 0 });
  };

  const handleSave = async (submitAfter: boolean) => {
    setError(null);
    if (form.amount < 1000) return setError('Số tiền tối thiểu 1.000đ');
    if (form.reason.length < 5) return setError('Diễn giải tối thiểu 5 ký tự');
    if (isAdvance && !form.advanceType)
      return setError('Tạm ứng: chọn loại (Có kế hoạch / Đột xuất)');
    if (isAdvanceCT && !ctLocation.trim())
      return setError('Công tác: nhập địa điểm.');
    if (isAdvanceThuong && form.amount > 5_000_000 && attachments.length === 0) {
      return setError('Tạm ứng > 5M cần kèm chứng từ (quotation/plan).');
    }
    if (!isSalaryAdvance && uploadingCount > 0) return setError('Đang upload file, chờ hoàn tất.');

    if (isBonus) {
      if (recipients.length === 0) return setError('Thưởng nóng: chọn ít nhất 1 người nhận.');
      const sum = recipients.reduce((s, r) => s + r.amount, 0);
      if (sum !== form.amount) {
        return setError(
          `Tổng thưởng (${sum.toLocaleString('vi-VN')}đ) không khớp Số tiền phiếu (${form.amount.toLocaleString('vi-VN')}đ).`,
        );
      }
    }

    setSaving(true);
    try {
      const payload: CreatePaymentInput = {
        ...form,
        advancePurpose: isAdvance ? advancePurpose : undefined,
        ctLocation: isAdvanceCT ? ctLocation.trim() : undefined,
        ctDays: isAdvanceCT ? ctDays : undefined,
        ctNhom: isAdvanceCT ? ctNhom : undefined,
        ctVung: isAdvanceCT ? ctVung : undefined,
        ctBudgetTotal: isAdvanceCT && ctBudget ? ctBudget.total : undefined,
        attachmentIds: isSalaryAdvance ? undefined : attachments.map((a) => a.id),
        recipients: isBonus
          ? recipients.map((r) => ({ employeeId: r.employeeId, amount: r.amount, note: r.note }))
          : undefined,
      };
      await onSubmit(payload, submitAfter);
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleFiles = async (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (attachments.length + arr.length > 5) {
      setError('Tối đa 5 tệp/phiếu.');
      return;
    }
    setError(null);
    setUploadingCount((c) => c + arr.length);
    for (const f of arr) {
      try {
        const up = await attachmentsApi.upload(f);
        setAttachments((prev) => [...prev, up]);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setUploadingCount((c) => c - 1);
      }
    }
  };

  const handleRemoveAttachment = async (id: string) => {
    // Nếu chưa link vào PR nào → BE cho xoá; nếu đã link (editing mode) → chỉ bỏ ra khỏi list.
    setAttachments((prev) => prev.filter((a) => a.id !== id));
    if (!editing) {
      try {
        await attachmentsApi.remove(id);
      } catch {
        /* best-effort */
      }
    }
  };

  const setType = (type: PaymentType) => {
    if (type === 'tam_ung') {
      setForm({ ...form, type, urgency: false, advanceType: 'planned' });
    } else {
      setForm({ ...form, type, advanceType: undefined });
    }
  };

  const setAdv = (advanceType: AdvanceType) => setForm({ ...form, advanceType });

  return (
    <div className="pfm-backdrop" onClick={onClose}>
      <div className="pfm-modal" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={(e) => { e.preventDefault(); handleSave(false); }}>
          {/* HEADER */}
          <div className="pfm-header">
            <div>
              <div className="pfm-breadcrumb">KẾ TOÁN › THANH TOÁN</div>
              <h2>{editing ? `Sửa phiếu ${editing.code}` : duplicateFrom ? `Sao chép từ ${duplicateFrom.code}` : 'Lập giấy đề nghị chi'}</h2>
              <p>{editing
                ? (editing.status === 'DRAFT'
                    ? 'Đang là bản nháp — sửa thoải mái trước khi gửi'
                    : 'KTT chưa duyệt — sửa xong lưu lại, phiếu vẫn ở trạng thái Chờ KTT duyệt')
                : duplicateFrom
                ? 'Đã fill sẵn từ phiếu nguồn — kiểm tra & sửa lại nếu cần trước khi gửi. Chứng từ cần upload lại.'
                : 'Điền thông tin để gửi Kế toán xử lý'}</p>
            </div>
            <button type="button" className="pfm-close" onClick={onClose} title="Đóng">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* BODY */}
          <div className="pfm-body">
            {/* Loại + Ngày cần chi */}
            <div className="pfm-grid-2">
              <div className="pfm-field">
                <label>Loại đề nghị <span className="req">*</span></label>
                <SearchableDropdown value={form.type} onChange={(value) => setType(value as PaymentType)} options={[{ value: 'chi_thuong', label: 'Thanh toán' }, { value: 'tam_ung', label: 'Tạm ứng' }, { value: 'ung_luong', label: 'Ứng lương' }, { value: 'hoan_ung', label: 'Hoàn ứng' }, { value: 'thuong_nong', label: 'Thưởng nóng' }]} ariaLabel="Loại đề nghị" />
              </div>
              <div className="pfm-field">
                <label>Ngày cần chi</label>
                <input type="date" value={reqDate} onChange={(e) => setReqDate(e.target.value)} />
              </div>
            </div>

            {/* Mục đích tạm ứng — chỉ show khi type=tam_ung */}
            {isAdvance && (
              <div className="pfm-field">
                <label>Mục đích tạm ứng <span className="req">*</span></label>
                <div className="pfm-purpose-cards">
                  <button
                    type="button"
                    className={`pfm-purpose-card ${advancePurpose === 'thuong' ? 'active' : ''}`}
                    onClick={() => setAdvancePurpose('thuong')}
                  >
                    <div className="pfm-purpose-head">
                      <div className={`pfm-radio ${advancePurpose === 'thuong' ? 'checked-blue' : ''}`} />
                      <span>Chi phí thường</span>
                    </div>
                    <div className="pfm-purpose-desc">
                      Mua vật tư, chi phí phát sinh, tạm ứng dự án…
                    </div>
                  </button>
                  <button
                    type="button"
                    className={`pfm-purpose-card ${advancePurpose === 'congtac' ? 'active' : ''}`}
                    onClick={() => setAdvancePurpose('congtac')}
                  >
                    <div className="pfm-purpose-head">
                      <div className={`pfm-radio ${advancePurpose === 'congtac' ? 'checked-blue' : ''}`} />
                      <span>Công tác</span>
                      <span className="pfm-badge-qcct">QCCT 2026</span>
                    </div>
                    <div className="pfm-purpose-desc">
                      Đi công tác tỉnh / nước ngoài · Áp dụng định mức theo QCCT
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Công tác sub-form + dự toán auto */}
            {isAdvanceCT && (
              <div className="pfm-ct-panel">
                <div className="pfm-grid-2">
                  <div className="pfm-field">
                    <label>Địa điểm công tác <span className="req">*</span></label>
                    <input
                      value={ctLocation}
                      onChange={(e) => setCtLocation(e.target.value)}
                      placeholder="VD: Đà Nẵng — họp khách hàng Vintech"
                    />
                  </div>
                  <div className="pfm-field">
                    <label>Số ngày công tác <span className="req">*</span></label>
                    <input
                      type="number"
                      min={1}
                      max={90}
                      value={ctDays}
                      onChange={(e) => setCtDays(Math.max(1, Number(e.target.value) || 1))}
                    />
                  </div>
                </div>
                <div className="pfm-grid-2">
                  <div className="pfm-field">
                    <label>Nhóm chức danh</label>
                    <SearchableDropdown value={String(ctNhom)} onChange={(value) => setCtNhom(Number(value) as QcctNhom)} options={([1, 2, 3, 4] as QcctNhom[]).map((item) => ({ value: String(item), label: NHOM_LABEL[item] }))} ariaLabel="Nhóm chức danh" />
                  </div>
                  <div className="pfm-field">
                    <label>Địa bàn</label>
                    <SearchableDropdown value={ctVung} onChange={(value) => setCtVung(value as QcctVung)} options={(['HN_HCM', 'TinhI', 'TinhKhac', 'Foreign'] as QcctVung[]).map((item) => ({ value: item, label: VUNG_LABEL[item] }))} ariaLabel="Địa bàn" />
                  </div>
                </div>

                {ctBudget && (
                  <div className="pfm-ct-budget">
                    <div className="pfm-ct-budget-head">
                      <div>
                        <b>Dự toán theo QCCT 2026</b>
                        <span className="pfm-ct-budget-sub">Tự tính từ nhóm × vùng × số ngày</span>
                      </div>
                    </div>
                    <div className="pfm-ct-budget-lines">
                      <div><span>Tiền ở ({Math.max(0, ctDays - 1)} đêm)</span><b>{formatVND(ctBudget.lodging)}</b></div>
                      <div><span>Phụ cấp lưu trú ({ctDays} ngày)</span><b>{formatVND(ctBudget.perDiem)}</b></div>
                      <div><span>Đi lại (khoán 2 chiều)</span><b>{formatVND(ctBudget.travel)}</b></div>
                      <div><span>Dự phòng 10%</span><b>{formatVND(ctBudget.contingency)}</b></div>
                    </div>
                    <div className="pfm-ct-budget-total">
                      <span>Tổng dự toán tối đa</span>
                      <b>{formatVND(ctBudget.total)}</b>
                    </div>
                  </div>
                )}

                <div className="pfm-bm03-link">
                  <div className="pfm-bm03-title"><span>♙</span><b>LINK QUYẾT ĐỊNH CÔNG TÁC TỪ HR</b><em>— NV/TP tạo trên module HR, KT chỉ chọn</em></div>
                  <div className="pfm-bm03-search"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4-4"/></svg><input value={bm03Search} onChange={(e) => setBm03Search(e.target.value)} placeholder="Tìm quyết định công tác đã duyệt (theo mã, NV, địa điểm)..." /></div>
                  <small>ⓘ Chỉ hiển thị quyết định công tác đã được TP/BGĐ duyệt trên HR. Không có? Yêu cầu NV/TP tạo trên HR trước.</small>
                </div>

                {ctExceedsQuota && (
                  <div className="pfm-warn pfm-warn-red">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#b91c1c" strokeWidth="2" strokeLinecap="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4M12 17h.01" /></svg>
                    <div>
                      <b>Vượt định mức QCCT:</b> Số tiền đang &gt; dự toán tối đa. Cần TGĐ/GĐ ký duyệt kèm thuyết minh.
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Số tiền — LARGE INPUT */}
            <div className="pfm-field">
              <label>Số tiền <span className="req">*</span></label>
              <div className="pfm-amount-wrap">
                <input
                  type="text"
                  inputMode="numeric"
                  value={amountInput}
                  onChange={(e) => handleAmount(e.target.value)}
                  placeholder="0"
                />
                <span className="pfm-amount-suffix">đ</span>
              </div>
            </div>

            {/* Người đề nghị + Bộ phận — auto-fill từ user đăng nhập */}
            <div className="pfm-field">
              <label>Người đề nghị <span className="pfm-label-note">(auto-fill từ user đăng nhập)</span></label>
              <div className="pfm-requester">
                <div className="pfm-requester-avatar">{initials}</div>
                <div className="pfm-requester-meta">
                  <div className="pfm-requester-name">{user?.name}</div>
                  <div className="pfm-requester-role">
                    {user?.roleLabel}
                    {user?.departmentName && ` · ${user.departmentName}`}
                    {user?.email && ` · ${user.email}`}
                  </div>
                </div>
              </div>
            </div>

            {/* Mức độ ưu tiên */}
            <div className="pfm-field">
              <label>
                Mức độ ưu tiên duyệt{' '}
                <span className="pfm-label-note">(quyết định queue: gấp trong ngày hay batch T6)</span>
              </label>
              {isAdvance ? (
                <div className="pfm-priority-cards">
                  <button
                    type="button"
                    className={`pfm-priority-card ${form.advanceType === 'planned' ? 'active' : ''}`}
                    onClick={() => setAdv('planned')}
                  >
                    <div className="pfm-priority-head">
                      <div className={`pfm-radio ${form.advanceType === 'planned' ? 'checked-blue' : ''}`} />
                      <span>Có kế hoạch</span>
                    </div>
                    <div className="pfm-priority-desc">
                      Công tác đã duyệt trước, dự án có budget → duyệt định kỳ thứ 6
                    </div>
                  </button>
                  <button
                    type="button"
                    className={`pfm-priority-card urgent ${form.advanceType === 'urgent' ? 'active' : ''}`}
                    onClick={() => setAdv('urgent')}
                  >
                    <div className="pfm-priority-head">
                      <div className={`pfm-radio ${form.advanceType === 'urgent' ? 'checked-red' : ''}`} />
                      <span>Đột xuất</span>
                      <span className="pfm-badge-urgent">GẤP</span>
                    </div>
                    <div className="pfm-priority-desc">
                      Sự cố cần chi gấp, hư hỏng, mua vật tư khẩn → duyệt trong ngày
                    </div>
                  </button>
                </div>
              ) : (
                <div className="pfm-priority-cards">
                  <button
                    type="button"
                    className={`pfm-priority-card ${!form.urgency ? 'active' : ''}`}
                    onClick={() => setForm({ ...form, urgency: false })}
                  >
                    <div className="pfm-priority-head">
                      <div className={`pfm-radio ${!form.urgency ? 'checked-blue' : ''}`} />
                      <span>Bình thường</span>
                    </div>
                    <div className="pfm-priority-desc">
                      Chi phí có thể chờ, không gấp → duyệt định kỳ thứ 6 hàng tuần
                    </div>
                  </button>
                  <button
                    type="button"
                    className={`pfm-priority-card urgent ${form.urgency ? 'active' : ''}`}
                    onClick={() => setForm({ ...form, urgency: true })}
                  >
                    <div className="pfm-priority-head">
                      <div className={`pfm-radio ${form.urgency ? 'checked-red' : ''}`} />
                      <span>Cần gấp</span>
                      <span className="pfm-badge-urgent">GẤP</span>
                    </div>
                    <div className="pfm-priority-desc">
                      Cần KTT/CEO xử lý ngay trong ngày, không thể chờ đến thứ 6
                    </div>
                  </button>
                </div>
              )}
            </div>

            {/* Bonus recipient picker (only for thuong_nong) */}
            {isBonus && (
              <BonusRecipientPicker
                totalAmount={form.amount}
                recipients={recipients}
                mode={bonusMode}
                onChange={setRecipients}
                onModeChange={setBonusMode}
              />
            )}

            {/* Warning boxes */}
            {showCeoWarn && (
              <div className="pfm-warn pfm-warn-yellow">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a16207" strokeWidth="2" strokeLinecap="round"><path d="M12 8v4M12 16h.01" /><circle cx="12" cy="12" r="10" /></svg>
                <div>
                  <b>Cần CEO ký duyệt:</b> Số tiền ≥ 3.000.000đ sẽ phải qua CEO ký sau khi KTT duyệt. Workflow: NV.KT lập PC → KTT ký → CEO ký → NV.KT xuất tiền.
                </div>
              </div>
            )}
            {showQuoteWarn && (
              <div className="pfm-warn pfm-warn-red">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#b91c1c" strokeWidth="2" strokeLinecap="round">
                  <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                  <path d="M12 9v4M12 17h.01" />
                </svg>
                <div>
                  <b>Bắt buộc kèm chứng từ:</b> Tạm ứng &gt; 5.000.000đ cần đính kèm quotation, plan hoặc chứng từ chứng minh mục đích. Nếu không có sẽ bị từ chối.
                </div>
              </div>
            )}

            {/* Lý do */}
            <div className="pfm-field">
              <div className="pfm-label-row">
                <label>Lý do chi <span className="req">*</span></label>
                <span className="pfm-counter">{form.reason.length} / 500</span>
              </div>
              <textarea
                rows={3}
                maxLength={500}
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="VD: Chi thanh toán tiền in ấn tài liệu quý 3 - Nhà in ABC..."
              />
            </div>

            {!isSalaryAdvance && <>
            {/* NCC + Bank */}
            <div className="pfm-grid-2">
              <div className="pfm-field">
                <label>NCC / Người nhận</label>
                <input
                  value={form.supplier || ''}
                  onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                  placeholder="VD: XYZ Trading Co"
                />
              </div>
              <div className="pfm-field">
                <label>Số TK ngân hàng</label>
                <input
                  value={form.bankAccount || ''}
                  onChange={(e) => setForm({ ...form, bankAccount: e.target.value })}
                  placeholder="VD: 0071 0004 12345"
                />
              </div>
            </div>
            <div className="pfm-grid-2">
              <div className="pfm-field">
                <label>Ngân hàng thụ hưởng</label>
                <SearchableDropdown value={form.recipientBankName || ''} onChange={(value) => setForm({ ...form, recipientBankName: value })} placeholder="Chọn ngân hàng" options={banks.map((bank) => ({ value: bank.name, label: bank.shortName ? `${bank.shortName} · ${bank.name}` : bank.name }))} ariaLabel="Ngân hàng thụ hưởng" />
              </div>
              <div className="pfm-field">
                <label>Chi nhánh ngân hàng</label>
                <input value={form.recipientBankBranch || ''} onChange={(e) => setForm({ ...form, recipientBankBranch: e.target.value })} placeholder="VD: Chi nhánh Hà Nội" />
              </div>
            </div>

            {/* Attachments dropzone */}
            <div className="pfm-field">
              <label>
                Chứng từ đính kèm
                {isAdvance && form.amount > 5_000_000 && (
                  <span className="pfm-label-note" style={{ color: '#b91c1c' }}>
                    {' '}(bắt buộc — tạm ứng &gt; 5M)
                  </span>
                )}
              </label>
              <div
                className={`pfm-dropzone ${dragOver ? 'drag-over' : ''}`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files);
                }}
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 13v8" />
                  <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" />
                  <path d="m8 17 4-4 4 4" />
                </svg>
                <span>Kéo thả file vào đây hoặc bấm để chọn</span>
                <small>PDF, JPG, PNG, DOCX, XLSX · tối đa 10MB · tối đa 5 tệp</small>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) handleFiles(e.target.files);
                  e.target.value = '';
                }}
              />

              {(attachments.length > 0 || uploadingCount > 0) && (
                <div className="pfm-attachment-list">
                  {attachments.map((a) => (
                    <div key={a.id} className="pfm-attachment-row">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6" />
                      </svg>
                      <span className="pfm-attachment-name">{a.fileName}</span>
                      <small>{Math.round(a.sizeBytes / 1024)} KB</small>
                      <button
                        type="button"
                        className="pfm-attachment-remove"
                        onClick={() => handleRemoveAttachment(a.id)}
                        title="Xoá"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                      </button>
                    </div>
                  ))}
                  {uploadingCount > 0 && (
                    <div className="pfm-attachment-row" style={{ color: '#64748b' }}>
                      Đang upload {uploadingCount} tệp…
                    </div>
                  )}
                </div>
              )}
            </div>

            </>}
            {error && <div className="pfm-error">⚠️ {error}</div>}
          </div>

          {/* FOOTER */}
          <div className="pfm-foot">
            <button type="button" className="pay-btn-outline" onClick={onClose}>
              Huỷ
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="pay-btn-outline"
                disabled={saving}
                onClick={() => handleSave(false)}
              >
                {saving ? 'Đang lưu...' : editing ? (editing.status === 'DRAFT' ? 'Cập nhật nháp' : 'Cập nhật') : 'Lưu nháp'}
              </button>
              {/* Chỉ show "+ Gửi duyệt" khi tạo mới hoặc đang sửa DRAFT.
                  Sửa PENDING_KTT thì đã gửi rồi — không submit lại được. */}
              {(!editing || editing.status === 'DRAFT') && (
                <button
                  type="button"
                  className="pay-btn-dark"
                  disabled={saving}
                  onClick={() => handleSave(true)}
                >
                  {saving ? 'Đang gửi...' : editing ? 'Cập nhật + Gửi duyệt' : 'Lưu + Gửi duyệt'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
